import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useToast } from '@/shared/contexts/ToastContext'

import {
  createHomecareVisit,
  endHomecarePatient,
  findHomecareCandidates,
  getHomeVisitLetterhead,
  getHomecarePatient,
  listHomecarePatients,
  patchHomecarePatient,
  registerHomecarePatient,
  type HomecarePatientInput,
} from './homecareApi'

const ROOT = 'homecare'
export const homecarePatientsKey = [ROOT, 'patients'] as const
export const homecarePatientKey = (patientId: string) =>
  [ROOT, 'patient', patientId] as const
export const homecareCandidatesKey = (query: string) =>
  [ROOT, 'candidates', query] as const
export const homeVisitLetterheadKey = (yakurekiId: string) =>
  [ROOT, 'letterhead', yakurekiId] as const
// 患者のページの保存。保存中の欄を画面が見分けるのに使う (useMutationState)。
export const homecarePatientSaveKey = (patientId: string) =>
  [ROOT, 'save', patientId] as const

// 左ペインの一覧。数人なので全部取る (検索の行は持たない)。
export const useHomecarePatients = () =>
  useQuery({ queryKey: homecarePatientsKey, queryFn: listHomecarePatients })

export const useHomecarePatient = (patientId: string | null) =>
  useQuery({
    queryKey: homecarePatientKey(patientId ?? ''),
    queryFn: () => getHomecarePatient(patientId!),
    enabled: patientId !== null,
  })

// 患者の欄・宛先を直すと、一覧と、出すときの頭書き (訪問のページ) の両方が古くなる。
const useInvalidateHomecare = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [ROOT] })
}

// 登録の画面の検索。打った語 (空白を除いて空なら引かない) で店の患者を探す。
// 打つたびに引き直すので、前の結果を出したまま次を待つ (行がちらつかない)
export const useHomecareCandidates = (query: string) => {
  const trimmed = query.trim()
  return useQuery({
    queryKey: homecareCandidatesKey(trimmed),
    queryFn: () => findHomecareCandidates(trimmed),
    enabled: trimmed !== '',
    placeholderData: (previous) => previous,
  })
}

export const useRegisterHomecarePatient = () => {
  const invalidate = useInvalidateHomecare()
  return useMutation({
    mutationFn: (patientId: string) => registerHomecarePatient(patientId),
    onSuccess: () => void invalidate(),
  })
}

export const useEndHomecarePatient = () => {
  const invalidate = useInvalidateHomecare()
  return useMutation({
    mutationFn: (homecarePatientId: string) =>
      endHomecarePatient(homecarePatientId),
    onSuccess: () => void invalidate(),
  })
}

// 患者のページからの保存。同じ患者への保存は直列にする (順序が入れ替わると結果が変わる)。
export const useUpdateHomecarePatient = (patientId: string) => {
  const invalidate = useInvalidateHomecare()
  const { showToast } = useToast()
  return useMutation({
    mutationKey: homecarePatientSaveKey(patientId),
    scope: { id: `homecare-patient:${patientId}` },
    mutationFn: (input: HomecarePatientInput) =>
      patchHomecarePatient(patientId, input),
    onSuccess: () => void invalidate(),
    onError: () => showToast('在宅の患者を保存できませんでした', 'error'),
  })
}

// ［訪問を記録］。作った器は共有の一覧 (ホームの最近の薬歴) にも出るので、そちらも取り直す。
export const useCreateHomecareVisit = () => {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateHomecare()
  return useMutation({
    mutationFn: (patientId: string) => createHomecareVisit(patientId),
    onSuccess: () => {
      void invalidate()
      void queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
    },
  })
}

export const useHomeVisitLetterhead = (yakurekiId: string | null) =>
  useQuery({
    queryKey: homeVisitLetterheadKey(yakurekiId ?? ''),
    queryFn: () => getHomeVisitLetterhead(yakurekiId!),
    enabled: yakurekiId !== null,
  })
